import { useCallback, useEffect, useRef } from 'react'
import { Loader2, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from '@/i18n'
import SortableHeader from './SortableHeader'
import TableSkeletonRows from './TableSkeletonRows'
import type { TagMappingSort } from './sort'
import type { EditMappingTarget } from './EditMappingDialog'
import type { MatchedTagGroup, MatchedTagRow } from '@/service/api'
import MatchBadge from './MatchBadge'
import RowActionsMenu, { type RowAction } from './RowActionsMenu'

interface Props {
  groups: MatchedTagGroup[]
  /**
   * Which row kind to show (Putra, 2026-09-17, P0). `''` shows both.
   *
   * Applied here rather than in the query: pagination is over SOURCE TAGS and
   * both kinds arrive inside the same group, so there is nothing to narrow
   * server-side and no page boundary to get wrong. A group left with no rows
   * is dropped, so the table does not show an empty tag.
   */
  sourceType: '' | 'timeseries' | 'document'
  /**
   * Open THE document an evidence line names, in the preview overlay, landing
   * on the passage that spells the tag (review E3: "open Documents for a
   * document mapping"; Greptile on #35127: the filename was plain text). The
   * mention still opens the tag's board, like every other row. Optional: a
   * caller with no overlay mounted renders the filename as text.
   */
  onSelectDocument?: (documentId: string, documentName: string, mention: string) => void
  /**
   * Open a match line's Source file — the timeseries file — in the board's
   * document preview, landed on the mapped tag's column. Optional: without it
   * the filename renders as text.
   */
  onSelectSourceFile?: (
    documentId: string,
    documentName: string,
    sourceType?: string | null,
    highlightTag?: string,
  ) => void
  loading: boolean
  /**
   * The load in flight will REPLACE these rows (review F10 / G3). The rows stay
   * on screen, dimmed, under an "Updating" status — not a blank table that
   * looks like a finished empty result. An appending page load leaves them as
   * they are and shows the spinner underneath.
   */
  reloading?: boolean
  hasMore: boolean
  searching: boolean
  onLoadMore: () => void
  /** `source` opens the drill-down on the tab the row was clicked from. */
  onSelectTag: (tag: string, source?: 'sheet' | 'document') => void
  /**
   * Open the Edit Mapping picker for a row. Optional so a caller that has no
   * picker mounted simply renders no menu, rather than a menu that does
   * nothing when clicked.
   */
  onEditMapping?: (target: EditMappingTarget) => void
  /**
   * Drop the mapping this row records. Optional on the same terms as
   * `onEditMapping` — a caller that cannot service it renders no such item.
   */
  onDeleteMapping?: (match: MatchedTagRow, tag: string) => void
  /**
   * Delete on a DOCUMENT-evidence line. That line has no match to reject, so
   * what is being dismissed is the tag itself — the same verdict Pending's
   * Delete records. Absent means the line renders no Delete item.
   */
  onRuleOutTag?: (tag: string) => void
  /**
   * A server facet (method, confidence, board) is narrowing the page. An empty
   * page then reads "nothing matches these filters", not "nothing mapped yet"
   * (Tag Mapping review 2026-09-28, D1).
   */
  filtered?: boolean
  /** Drops every facet. Rendered as the empty state's action when `filtered`. */
  onClearFilters?: () => void
  /**
   * The active column sort, applied by the SERVER (review F09). Optional with
   * `onSort`: a caller that does not sort renders plain-text headers.
   */
  sort?: TagMappingSort | null
  onSort?: (key: string) => void
}

/**
 * One rendered line of the table.
 *
 * A source tag contributes one `match` line per tag matched to it, plus at most
 * one `document` line when the tenant's prose names it. They share a shape so
 * the table stays a flat list — the grouping now lives in the repeated
 * left-hand cell rather than in nested markup.
 */
type Line =
  | { kind: 'match'; tag: string; match: MatchedTagRow }
  | {
      kind: 'document'
      tag: string
      /** ONE document that names the tag (review F00: one line per document). */
      documentId: string
      documentName: string
      mention: string
      passageCount: number
      /** Mean confidence over this document's passages; absent only when none carried one. */
      confidence?: number | null
    }

const linesFor = (
  group: MatchedTagGroup,
  sourceType: '' | 'timeseries' | 'document',
): Line[] => {
  const lines: Line[] = []
  if (sourceType !== 'document') {
    lines.push(
      ...group.matches
        // A DELETED mapping is gone, not struck through. It used to render at
        // half opacity with a line through it, which reads as a row that is
        // still there and still means something — Pratima: "when we remove any
        // mapping it should be removed permanently instead there is a cut and
        // it becomes blurry".
        //
        // The feedback row behind it stays, and that is the point: `rejected`
        // is what stops the matcher re-proposing the pair, and re-connecting
        // the same pair writes a NEWER `accepted` row that supersedes it
        // (`connect_tag`), so a deleted mapping that is added again simply
        // comes back. Only the rendering of the tombstone goes.
        //
        // A tag whose ONLY match is rejected never reaches this table at all —
        // `NOT_REJECTED_SQL` filters the page query — so dropping these lines
        // cannot empty a group that the server thought had rows.
        .filter((match) => match.decision !== 'rejected')
        // Read the row's OWN type rather than assuming every match is a
        // timeseries one. `source_type` is nullable -- an FS-675 asked-about
        // tag has no source file to type and renders an em-dash -- and keeping
        // those under "Time Series" would file a row under a type its own cell
        // denies. A future non-timeseries match lands correctly for free.
        .filter((match) => !sourceType || match.source_type === sourceType)
        .map((match) => ({
          kind: 'match' as const,
          tag: group.tag,
          match,
        })),
    )
  }
  if (sourceType !== 'timeseries') {
    // One line PER DOCUMENT, not one "13 documents, 34 passages" summary
    // (Tag Mapping review 2026-09-28, F00): the reviewer sees which file says
    // what, with that file's own score.
    //
    // `Array.isArray`, not `?? []`: during a staggered rollout this bundle can
    // be answered by a backend still sending the old single-object summary, and
    // iterating an object throws for the whole table. An old shape draws no
    // evidence lines rather than no table (Greptile, #35127).
    const documents = Array.isArray(group.documents) ? group.documents : []
    for (const document of documents) {
      lines.push({
        kind: 'document',
        tag: group.tag,
        documentId: document.document_id,
        documentName: document.document_name,
        mention: document.mention,
        passageCount: document.passage_count,
        confidence: document.confidence,
      })
    }
  }
  return lines
}

/**
 * Matched tab: one line per matched pair.
 *
 * The API still groups server-side and paginates over SOURCE tags, so a group
 * can never straddle a page boundary and be rendered twice. The flattening is
 * presentational — the left-hand tag repeats down its own lines rather than
 * spanning them, which is what lets every other column line up in a plain grid.
 */
const MatchedTable = ({
  groups,
  sourceType,
  loading,
  reloading = false,
  hasMore,
  searching,
  onLoadMore,
  onSelectTag,
  onSelectDocument,
  onSelectSourceFile,
  onEditMapping,
  onDeleteMapping,
  onRuleOutTag,
  filtered = false,
  onClearFilters,
  sort = null,
  onSort,
}: Props) => {
  const { t } = useTranslation()
  const observerRef = useRef<IntersectionObserver | null>(null)
  const onLoadMoreRef = useRef(onLoadMore)
  onLoadMoreRef.current = onLoadMore

  /**
   * A CALLBACK ref, not a `useRef` + effect keyed on `[onLoadMore, hasMore]`.
   *
   * The Source type facet can empty a page, which takes the early return below
   * and unmounts the sentinel. Clearing the facet mounts a NEW element -- but
   * neither `onLoadMore` nor `hasMore` changed, because the facet never touches
   * the query. The effect therefore did not re-run and the observer stayed
   * attached to the detached node: infinite scroll was dead until something
   * unrelated moved a dependency, and the manual Load more had gone with the
   * empty state. React calls this on every mount and unmount of the node
   * itself, which is the thing that actually changed.
   */
  const attachSentinel = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        // Read through a ref so a new `onLoadMore` identity does not force the
        // node to be re-observed -- the callback ref only fires on node change.
        if (entries[0].isIntersecting) onLoadMoreRef.current()
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    observerRef.current = observer
  }, [])

  // Tear the observer down with the component, not just with the node.
  useEffect(() => () => observerRef.current?.disconnect(), [])

  /**
   * The rows this page will actually draw, after the Source type facet.
   *
   * Computed BEFORE the empty state, because `groups.length` is the unfiltered
   * count: a page whose every group is hidden by the facet would otherwise
   * render a table with a header and nothing under it, and no explanation.
   */
  const lines = groups.flatMap((group) => linesFor(group, sourceType))
  const hiddenByFilter = !loading && lines.length === 0 && groups.length > 0

  if (!loading && lines.length === 0) {
    return (
      <div className="px-6 py-16 text-center text-sm text-muted-foreground">
        {hiddenByFilter
          ? t('data_catalog:tag_mapping.empty.no_rows_for_source_type', {
              defaultValue:
                'No rows of this source type on the tags loaded so far. Load more, or clear the filter.',
            })
          : searching
            ? t('data_catalog:tag_mapping.empty.no_results', {
                defaultValue: 'No tags match your search.',
              })
            : filtered
              ? t('data_catalog:tag_mapping.empty.no_rows_for_filters', {
                  defaultValue: 'No mappings match these filters.',
                })
              : t('data_catalog:tag_mapping.empty.matched', {
                  defaultValue: 'No matched tags yet.',
                })}
        {!hiddenByFilter && !searching && filtered && onClearFilters && (
          <div className="mt-4">
            <button
              type="button"
              onClick={onClearFilters}
              className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted"
            >
              {t('data_catalog:tag_mapping.empty.clear_filters', { defaultValue: 'Clear filters' })}
            </button>
          </div>
        )}
        {hiddenByFilter && hasMore && (
          <div className="mt-4">
            <button
              type="button"
              onClick={onLoadMore}
              className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted"
            >
              {t('data_catalog:tag_mapping.load_more', { defaultValue: 'Load more' })}
            </button>
          </div>
        )}
      </div>
    )
  }

  /**
   * `documents.type` as a label. Reuses the catalog's own file-type vocabulary
   * rather than a second one private to this table, so "Time Series" here and
   * in the Data Catalog filter can never drift apart. An unrecognised type
   * falls back to the raw value — better a lowercase word than a blank cell.
   */
  const sourceTypeLabel = (type?: string | null): string => {
    if (!type) return '—'
    // Widened to `string` deliberately: a template literal narrows to a literal
    // key type, which sends i18next's overloads down the typed-resource branch
    // and resolves `t` to a union of every value shape in the bundle.
    const key: string = `data_catalog:doc_types.${type.toLowerCase()}`
    return t(key, { defaultValue: type })
  }

  /**
   * The kebab's items for one matched line — "Edit" then "Delete", the order
   * frame 1232:285266 draws them in. An action whose handler the page did not
   * supply is left out rather than rendered inert, and a row left with none
   * shows no kebab at all.
   */
  const matchActions = (tag: string, match: MatchedTagRow): RowAction[] => {
    const actions: RowAction[] = []
    if (onEditMapping) {
      actions.push({
        key: 'edit',
        label: t('data_catalog:tag_mapping.row_actions.edit', { defaultValue: 'Edit' }),
        icon: <Pencil aria-hidden />,
        onSelect: () =>
          onEditMapping({
            tag,
            displayTag: tag,
            // The write is keyed on the P&ID the tag was read FROM, not on the
            // timeseries document it currently points at.
            documentId: match.pid_document_id ?? null,
            // THIS mapping is what Edit replaces (review F34): the server
            // retires it once the new one is saved. Only this line — a tag
            // mapped to several signals keeps its other lines.
            replaces: { matchId: match.match_id },
          }),
      })
    }
    // NOT offered on an already-rejected sibling. Such a row is no longer
    // drawn at all (`linesFor`), and deleting it would re-submit the same
    // `rejected` decision: no state change behind a "deleted" toast.
    if (onDeleteMapping && match.decision !== 'rejected') {
      actions.push({
        key: 'delete',
        label: t('data_catalog:tag_mapping.row_actions.delete', { defaultValue: 'Delete' }),
        icon: <Trash2 aria-hidden />,
        onSelect: () => onDeleteMapping(match, tag),
      })
    }
    return actions
  }

  /**
   * The kebab on a document-evidence line — Edit and Delete, the pair Figma
   * section 11 gives every row that carries a confidence.
   *
   * It used to have no kebab at all, on the reasoning that evidence has no
   * mapping to re-point or drop. Both items DO mean something here:
   *
   * * Edit opens the same picker every other row opens, so the tag this
   *   document names can be mapped to a counterpart. `documentId` is null
   *   because the source is prose — only a DRAWING's id may travel as the
   *   match's `pid_document_id`.
   * * Delete rules the TAG out, which is what Pending's Delete does for the
   *   same reason: there is no mapping to reject, so the thing being dismissed
   *   is the identity.
   */
  const documentActions = (tag: string): RowAction[] => {
    const actions: RowAction[] = []
    if (onEditMapping) {
      actions.push({
        key: 'edit',
        label: t('data_catalog:tag_mapping.row_actions.edit', { defaultValue: 'Edit' }),
        icon: <Pencil aria-hidden />,
        onSelect: () => onEditMapping({ tag, displayTag: tag, documentId: null }),
      })
    }
    if (onRuleOutTag) {
      actions.push({
        key: 'delete',
        label: t('data_catalog:tag_mapping.row_actions.delete', { defaultValue: 'Delete' }),
        icon: <Trash2 aria-hidden />,
        onSelect: () => onRuleOutTag(tag),
      })
    }
    return actions
  }

  // First rows still on the way: placeholder rows, not headers over nothing.
  const coldLoad = loading && lines.length === 0
  // Rows on screen about to be replaced: keep them, dim them, say so.
  const updating = reloading && lines.length > 0

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      {/* Screen-reader only: sighted users see the rows dim. A visible
          "Updating…" line on every sort click was noise. */}
      {updating && (
        <p role="status" aria-live="polite" className="sr-only">
          {t('data_catalog:tag_mapping.table.updating', { defaultValue: 'Updating…' })}
        </p>
      )}
      <table className="w-full table-fixed text-sm">
        <colgroup>
          {/*
            Figma draws this table at TWO widths -- 1072 with the Data sidebar
            open (1232:285142) and 1328 with it closed (1232:285884) -- and the
            difference between them is the spec:

              1072: 160 / 160 / 350 / 191 / 165 / 46
              1328: 200 / 200 / 526 / 191 / 165 / 46
              delta: +40  +40  +176    0     0    0

            The last three are FIXED px: they do not move when 256px of sidebar
            appears. The first two are 15% at both widths (14.93 / 15.06).
            Source file absorbs everything else.

            Percentages for all six was wrong: it made Source file 425px at the
            1072 the app actually renders at, against the 350 the frame draws,
            and that 75px landed as dead space in front of Source type.
          */}
          <col className="w-[15%]" />
          <col className="w-[15%]" />
          {/* No width: fixed table layout hands it the remainder. */}
          <col />
          <col className="w-[191px]" />
          <col className="w-[165px]" />
          <col className="w-[46px]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-background dark:bg-dark-card">
          <tr className="border-b border-border text-left text-sm text-muted-foreground">
            {/* Review F09 / B1: all five headings sort the WHOLE filtered set
                (`MATCHED_SORT_KEYS`); the server orders each tag by what its
                top row shows. */}
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.pid_tag', { defaultValue: 'P&ID tag' })}
              sortKey="pid_tag"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.mapped_tag', { defaultValue: 'Mapped tag' })}
              sortKey="mapped_tag"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.source_file', { defaultValue: 'Source file' })}
              sortKey="source_file"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.source_type', { defaultValue: 'Source type' })}
              sortKey="source_type"
              sort={sort}
              onSort={onSort}
            />
            <SortableHeader
              label={t('data_catalog:tag_mapping.column.confidence', { defaultValue: 'Confidence' })}
              sortKey="confidence"
              sort={sort}
              onSort={onSort}
            />
            {/* The actions column is unlabelled in the frame; the header still
                exists so the column count matches and screen readers get a name. */}
            <th className="px-2 py-3 font-normal">
              <span className="sr-only">
                {t('data_catalog:tag_mapping.column.actions', { defaultValue: 'Actions' })}
              </span>
            </th>
          </tr>
        </thead>
        <tbody className={updating ? 'opacity-60 transition-opacity' : undefined} aria-busy={updating || undefined}>
          {coldLoad && <TableSkeletonRows />}
          {lines.map((line) => {
              const key =
                line.kind === 'match'
                  ? line.match.match_id
                  : `${line.tag}:document:${line.documentId}`

              return (
                <tr
                  key={key}
                  // `group/row` + `hover:bg-accent`: the frame highlights the
                  // whole hovered row and draws its kebab only there.
                  className="group/row border-b border-border hover:bg-accent"
                >
                  <td className="truncate px-6 py-3">
                    <button
                      type="button"
                      onClick={() => onSelectTag(line.tag)}
                      title={line.tag}
                      className="max-w-full truncate text-left text-sm text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {line.tag}
                    </button>
                  </td>

                  {line.kind === 'match' ? (
                    <>
                      <td className="truncate px-6 py-3">
                        {/* Plain text (Pegah, 2026-10-13 review): it opened the
                            same file Source file opens, in a second viewer.
                            Source file is the one link to the file. */}
                        <span
                          title={line.match.tag}
                          className="block truncate text-sm text-foreground"
                        >
                          {line.match.tag}
                        </span>
                      </td>
                      <td
                        className="truncate px-6 py-3 text-foreground"
                        title={line.match.ts_document_name ?? undefined}
                      >
                        {line.match.ts_document_id && line.match.ts_document_name && onSelectSourceFile ? (
                          <button
                            type="button"
                            onClick={() =>
                              onSelectSourceFile(
                                line.match.ts_document_id as string,
                                line.match.ts_document_name as string,
                                line.match.source_type,
                                // The column this tag is in the file: the
                                // same amber highlight the mapped-tag link
                                // used to land on.
                                line.match.tag,
                              )
                            }
                            className="max-w-full truncate text-left text-sm text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {line.match.ts_document_name}
                          </button>
                        ) : (
                          (line.match.ts_document_name ?? '—')
                        )}
                      </td>
                      <td className="truncate px-6 py-3 text-foreground">
                        {sourceTypeLabel(line.match.source_type)}
                      </td>
                      <td className="px-6 py-3">
                        <MatchBadge
                          confidence={line.match.confidence}
                          matchMethod={line.match.match_method}
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      {/* Evidence, not a match — a document discusses the tag,
                          it does not claim an identity. So no confidence badge
                          and nothing to accept: the counts lead to the
                          passages. */}
                      <td className="truncate px-6 py-3">
                        {/* Plain text, like a match's mapped tag: the P&ID tag
                            cell already opens this tag's board. */}
                        <span
                          title={line.mention}
                          className="block truncate text-sm text-foreground"
                        >
                          {line.mention}
                        </span>
                      </td>
                      {/* The document itself, in the Source file column — it IS
                          the source this line's evidence was read from. The
                          passage count rides in the tooltip. */}
                      <td
                        className="truncate px-6 py-3 text-foreground"
                        title={t('data_catalog:tag_mapping.detail.documents.passage_count', {
                          count: line.passageCount,
                          defaultValue: '{{count}} passages',
                        })}
                      >
                        {onSelectDocument ? (
                          <button
                            type="button"
                            onClick={() =>
                              onSelectDocument(line.documentId, line.documentName, line.mention)
                            }
                            className="max-w-full truncate text-left text-sm text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {line.documentName}
                          </button>
                        ) : (
                          line.documentName
                        )}
                      </td>
                      <td className="truncate px-6 py-3 text-foreground">
                        {sourceTypeLabel('document')}
                      </td>
                      {/* Evidence DOES carry a confidence: the extractor's
                          score that this passage named the tag. It is not a
                          claim that two identifiers are the same device — that
                          is what a match asserts — so the row still offers
                          nothing to accept or reject. The em-dash stays for a
                          tenant whose passages were written before the score
                          was recorded, where there is genuinely no number. */}
                      <td className="px-6 py-3 text-muted-foreground">
                        {/* A real score on the same scale as a match: 0.97/0.93
                            for a registry hit (exact spelling vs one reduced to
                            the device core), 0.92/0.80/0.75 for a discovery.
                            The em-dash is for rows scanned before the scanner
                            scored registry hits, until their document is
                            re-scanned. */}
                        {line.confidence === undefined || line.confidence === null ? (
                          '—'
                        ) : (
                          <MatchBadge confidence={line.confidence} />
                        )}
                      </td>
                    </>
                  )}
                  {/* Row actions. Both kinds carry them now: section 11 gives
                      Edit + Delete to every row with a confidence, and a
                      document-evidence line has one. See `documentActions`. */}
                  <td className="px-2 py-3">
                    <RowActionsMenu
                      label={t('data_catalog:tag_mapping.row_actions.label', {
                        defaultValue: 'Row actions',
                      })}
                      actions={
                        line.kind === 'match'
                          ? matchActions(line.tag, line.match)
                          : documentActions(line.tag)
                      }
                    />
                  </td>
                </tr>
              )
          })}
        </tbody>
      </table>

      {/* The spinner is for a page APPENDING below rows already on screen; a
          cold load draws skeleton rows and a replacing load says "Updating". */}
      {loading && !coldLoad && !updating && (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}
      {/* Keyed so the empty state's button wrapper, which lands at the same
          child index, can never inherit this node: React would keep the DOM
          element and re-attach the ref to it, and the round trip
          filter -> empty -> All would re-observe the OLD sentinel. */}
      {hasMore && <div key="sentinel" ref={attachSentinel} className="h-1" />}
    </div>
  )
}

export default MatchedTable
